import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, ThumbsUp, ThumbsDown, Play, TrendingUp, Music2 } from "lucide-react";
import { useSmartMusicRecommendations } from "@/hooks/useSmartMusicRecommendations";
import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

interface SmartRecommendationsProps {
  currentBPM?: number;
  mood?: string;
  timeOfDay?: string;
  weather?: string;
  onPlayTrack?: (track: string, artist: string) => void;
}

export const SmartRecommendations = ({
  currentBPM,
  mood,
  timeOfDay,
  weather,
  onPlayTrack,
}: SmartRecommendationsProps) => {
  const { recommendations, insights, isLoading, generateRecommendations, saveFeedback } = 
    useSmartMusicRecommendations();
  const [feedbackGiven, setFeedbackGiven] = useState<Set<string>>(new Set());

  const handleGenerate = async () => {
    await generateRecommendations({
      bpm: currentBPM,
      mood,
      timeOfDay,
      weather,
    });
  };

  const handleFeedback = async (track: string, artist: string, feedback: 'like' | 'dislike') => {
    const key = `${track}-${artist}`;
    setFeedbackGiven(prev => new Set([...prev, key]));
    await saveFeedback(track, artist, feedback, { 
      bpm: currentBPM, 
      mood, 
      timeOfDay, 
      weather 
    });
  };

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Recomendações Inteligentes</h3>
          <Badge variant="outline" className="gap-1">
            <Music2 className="w-3 h-3" />
            IA
          </Badge>
        </div>
        <Button
          onClick={handleGenerate}
          disabled={isLoading}
          size="sm"
          className="gap-2"
        >
          <Sparkles className="w-4 h-4" />
          {isLoading ? 'Gerando...' : 'Gerar Recomendações'}
        </Button>
      </div>

      {insights && (
        <div className="grid grid-cols-2 gap-3 p-4 bg-muted/30 rounded-lg">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">BPM Preferido</p>
            <p className="text-sm font-medium">{insights.preferred_bpm_range}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Energia</p>
            <p className="text-sm font-medium capitalize">{insights.energy_preference}</p>
          </div>
          <div className="col-span-2 space-y-1">
            <p className="text-xs text-muted-foreground">Gêneros Favoritos</p>
            <div className="flex flex-wrap gap-1">
              {insights.favorite_genres.map((genre, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">
                  {genre}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      <Separator />

      <ScrollArea className="h-[400px] pr-4">
        <div className="space-y-3">
          {recommendations.length === 0 && !isLoading && (
            <div className="text-center py-8 text-muted-foreground">
              <Sparkles className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Clique em "Gerar Recomendações" para descobrir músicas perfeitas para você</p>
            </div>
          )}

          {recommendations.map((rec, idx) => {
            const key = `${rec.track}-${rec.artist}`;
            const hasFeedback = feedbackGiven.has(key);

            return (
              <Card key={idx} className="p-4 space-y-3 hover:bg-accent/50 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium truncate">{rec.track}</h4>
                      {rec.discovery && (
                        <Badge variant="outline" className="text-xs gap-1">
                          <TrendingUp className="w-3 h-3" />
                          Descoberta
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{rec.artist}</p>
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                      {rec.reason}
                    </p>
                  </div>
                  {onPlayTrack && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onPlayTrack(rec.track, rec.artist)}
                      className="shrink-0"
                    >
                      <Play className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Music2 className="w-3 h-3" />
                      {rec.bpm} BPM
                    </span>
                    <span>{rec.genre}</span>
                    <span>Energia: {Math.round(rec.energy * 100)}%</span>
                  </div>

                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant={hasFeedback ? "secondary" : "ghost"}
                      onClick={() => handleFeedback(rec.track, rec.artist, 'like')}
                      disabled={hasFeedback}
                      className="h-7 w-7 p-0"
                    >
                      <ThumbsUp className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant={hasFeedback ? "secondary" : "ghost"}
                      onClick={() => handleFeedback(rec.track, rec.artist, 'dislike')}
                      disabled={hasFeedback}
                      className="h-7 w-7 p-0"
                    >
                      <ThumbsDown className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </ScrollArea>

      {insights && (
        <div className="pt-4 border-t">
          <p className="text-xs text-muted-foreground">
            <strong>Padrão de Escuta:</strong> {insights.listening_patterns}
          </p>
        </div>
      )}
    </Card>
  );
};
